import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify, { Config } from 'dompurify';
import { AI_CONTRACT_CLASSES, ChatHtmlTranslatorService } from './chat-html-translator.service';

/**
 * Allowlist-based DOMPurify configuration for LLM-generated rich HTML.
 *
 * The bot HTML is built by the LLM over document content, so it is treated as
 * UNTRUSTED. DOMPurify removes `<script>`, `on*` handlers, `javascript:`/`data:`
 * URLs, `<iframe>`/`<object>`/`<embed>`, and crucially the inline `style`
 * attribute: the LLM no longer dictates colors or layout, the front-end design
 * system does. Only the safe structure plus a fixed vocabulary of semantic CSS
 * classes survives, and even that class list is narrowed by a hook because
 * DOMPurify does not filter class VALUES on its own. We never trust raw,
 * unsanitized HTML.
 */
const SAFE_HTML_CONFIG: Config = {
  ALLOWED_TAGS: [
    'div', 'section', 'article', 'header', 'footer',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'span', 'strong', 'em', 'b', 'i', 'u', 'br', 'hr', 'small', 'blockquote',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'a'
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'colspan', 'rowspan', 'data-task', 'class'],
  ALLOWED_URI_REGEXP: /^https?:\/\//i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math'],
  FORBID_ATTR: ['style', 'srcset', 'src', 'onerror', 'onload', 'onclick'],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
  RETURN_TRUSTED_TYPE: false
};

/** Fixed vocabulary of semantic classes the sanitized HTML is allowed to keep. */
const ALLOWED_CLASS_SET = new Set<string>(AI_CONTRACT_CLASSES);

@Injectable({
  providedIn: 'root'
})
export class ChatMessageTextService {
  constructor(
    private sanitizer: DomSanitizer,
    private translator: ChatHtmlTranslatorService
  ) {
    this.registerSafeLinkHook();
    this.registerContractClassHook();
  }

  normalize(html: string): string {
    try {
      const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html || '') || '';
      const withLineBreaks = sanitized
        .replaceAll(/<br\s*\/?>/gi, '\n')
        .replaceAll(/<\/(p|div|li|ul|ol|h[1-6])>/gi, '\n');
      const withoutTags = withLineBreaks.replaceAll(/<[^>]+>/g, '');
      return this.decodeHtmlEntities(withoutTags).replaceAll(/\n{3,}/g, '\n\n').trim();
    } catch {
      return html || '';
    }
  }

  /**
   * Renders LLM-generated HTML for the chat using the front-end design system:
   * `sanitize` (strip scripts, handlers, inline styles, non-contract classes) →
   * `translate` (normalize to the canonical `ai-answer` structure) → `trust`.
   *
   * The look comes entirely from the front-end theme, never from the LLM markup.
   * If the sanitized HTML cannot be normalized, it falls back to the cleaned markup
   * so rendering degrades gracefully instead of breaking.
   *
   * This method is stateless; per-input memoization for re-renders is handled by the
   * pure {@link SafeBotHtmlPipe} that wraps it in the template.
   */
  toSafeHtml(content: string): SafeHtml {
    const clean = DOMPurify.sanitize(content || '', SAFE_HTML_CONFIG) as string;
    const canonical = this.translator.translate(clean);
    return this.sanitizer.bypassSecurityTrustHtml(canonical ?? clean);
  }

  /**
   * Forces every surviving anchor to open safely: external target and
   * noopener/noreferrer to avoid reverse-tabnabbing. Anchors with unsafe schemes
   * have already been stripped by ALLOWED_URI_REGEXP at this point.
   */
  private registerSafeLinkHook(): void {
    DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }

  /**
   * Filters class VALUES against the semantic contract. DOMPurify keeps the `class`
   * attribute but never validates its tokens, so an LLM could otherwise smuggle
   * arbitrary theme classes. Any token outside {@link AI_CONTRACT_CLASSES} is dropped;
   * an element left with no valid class loses the attribute entirely.
   */
  private registerContractClassHook(): void {
    DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
      if (!node.hasAttribute('class')) {
        return;
      }
      const kept = (node.getAttribute('class') || '')
        .split(/\s+/)
        .filter((token) => ALLOWED_CLASS_SET.has(token));
      if (kept.length) {
        node.setAttribute('class', kept.join(' '));
      } else {
        node.removeAttribute('class');
      }
    });
  }

  private decodeHtmlEntities(value: string): string {
    return value
      .replaceAll(/&nbsp;/gi, ' ')
      .replaceAll(/&amp;/gi, '&')
      .replaceAll(/&lt;/gi, '<')
      .replaceAll(/&gt;/gi, '>')
      .replaceAll(/&quot;/gi, '"')
      .replaceAll(/&#39;/gi, "'")
      .replaceAll(/&#(\d+);/g, (_match, code) => String.fromCharCode(Number(code)))
      .replaceAll(/&#x([0-9a-f]+);/gi, (_match, code) => String.fromCharCode(parseInt(code, 16)));
  }
}
